// Lightweight top loading bar shown during View Transitions navigations
// Inspired by common progress bars but adapted for this theme

/** @typedef {HTMLElement & { style: CSSStyleDeclaration }} HTMLElementWithStyle */

class PageProgressBar {
  constructor() {
    this.progress = 0;
    this.trickleTimer = null;
    /** @type {HTMLElementWithStyle | null} */
    this.wrapper = null;
    /** @type {HTMLElementWithStyle | null} */
    this.progressEl = null;
    this.isStarted = false;
    this.lastSetAt = 0;
    this.lastProgress = 0;
    this.startedAt = 0;
    this.minVisibleMs = 500;
  }

  ensureStyles() {
    if (document.getElementById('page-progress-styles')) return;
    const style = document.createElement('style');
    style.id = 'page-progress-styles';
    style.textContent = `
.page-progress { --progress-height: .1875rem; --progress-top: 0; --radius-full: 9999px; --color-beam-high: rgba(255,255,255,.9); --color-beam-medium: rgba(255,255,255,.65); --color-beam-low: rgba(255,255,255,.35); --color-beam-off: rgba(255,255,255,0); --color-glow-high: rgba(255,255,255,.9); --color-glow-medium: rgba(255,255,255,.65); --color-glow-low: rgba(255,255,255,.35); --color-glow-off: rgba(255,255,255,0); position: fixed; z-index: 9999; top: var(--progress-top); left: 0; overflow: hidden visible; width: 100%; opacity: 0; transition: opacity .4s linear; transform-origin: 0 0; pointer-events: none; view-transition-name: page-progress-bar }
@media (prefers-color-scheme: dark){ .page-progress { --color-beam-high: rgba(255,255,255,.5); --color-beam-medium: rgba(255,255,255,.35); --color-beam-low: rgba(255,255,255,.25); --color-beam-off: rgba(255,255,255,0); --color-glow-high: rgba(255,255,255,.6); --color-glow-medium: rgba(255,255,255,.4); --color-glow-low: rgba(255,255,255,.25); --color-glow-off: rgba(255,255,255,0) } }
.page-progress::view-transition { z-index: 2147483647 }
::view-transition-group(page-progress-bar) { z-index: 2147483647 }
::view-transition-image-pair(page-progress-bar) { z-index: 2147483647 }
.page-progress--visible { opacity: 1; transition: opacity .1s linear }
.page-progress__progress { position: fixed; top: var(--progress-top); left: 0; width: 100.5vw; height: var(--progress-height); border-radius: var(--radius-full); transform: translateX(-100%); transition: transform .3s cubic-bezier(.3,0,.05,1) }
.page-progress__progress--instant { transition: none }
.page-progress__progress--complete { transition-duration: .2s }
.page-progress__progress--rushing { transition: transform .2s linear }
.page-progress__barBackground { width: 100%; height: 100%; border-radius: var(--radius-full); background: linear-gradient(to right,var(--color-beam-low) 0%,var(--color-beam-medium) 100%); mix-blend-mode: difference }
.page-progress__tipContainer { position: absolute; right: 0; top: 0; opacity: 1; border-radius: var(--radius-full) }
.page-progress__tip { width: 30rem; height: var(--progress-height); border-radius: var(--radius-full); background: linear-gradient(to right,var(--color-beam-off) 0%,var(--color-beam-high) 100%); mix-blend-mode: difference }
.page-progress__glowContainer { position: absolute; top: 50%; right: 0; width: 37.5rem; height: var(--progress-height); transform: translateY(-50%); opacity: .6; animation: page-progress-glow-pulse 1s 3.5s ease-in-out infinite }
.page-progress__glow { position: absolute; top: 50%; right: 0; width: 100%; height: 100%; border-radius: var(--radius-full); transform: translateY(-50%); background: linear-gradient(to right,var(--color-glow-off) 0%,var(--color-glow-high) 100%); mix-blend-mode: difference }
.page-progress__glow.g1 { right: -.0625rem; width: 10%; filter: blur(.0625rem) }
.page-progress__glow.g2 { right: -.125rem; width: 15%; filter: blur(.1875rem) }
.page-progress__glow.g3 { filter: blur(.125rem) }
.page-progress__glow.g4 { height: 200%; filter: blur(.3125rem) }
.page-progress__glow.g5 { right: -.625rem; height: 400%; filter: blur(.5rem) }
.page-progress__glow.g6 { right: -1.25rem; height: 600%; filter: blur(1.25rem) }
.page-progress__glow.g7 { right: -4.6875rem; height: 1500%; filter: blur(2.5rem) }
.page-progress__gradientWrapper { position: absolute; top: 0; overflow: hidden; width: 100%; height: 100%; border-radius: var(--radius-full); opacity: 1 }
.page-progress__gradientAnimation { width: 100%; height: 100%; border-radius: var(--radius-full); transform: translateX(-100%); background: linear-gradient(to right,var(--color-beam-off) 0%,var(--color-beam-off) 25%,var(--color-beam-high) 50%,var(--color-beam-off) 60%,var(--color-beam-off) 100%); animation: page-progress-swoosh 1s 3.5s ease-out infinite; mix-blend-mode: difference }
@keyframes page-progress-swoosh { 0%{ transform: translateX(-100%) } 100%{ transform: translateX(100%) } }
@keyframes page-progress-glow-pulse { 0%,50%{ opacity:.6 } 60%{ opacity:1 } 90%,100%{ opacity:.6 } }
/* SWATI: only animate while the bar is showing. Hidden it is just opacity 0, and its infinite glow/swoosh keyframes drive 7 blurred layers with mix-blend-mode: difference, which WebKit recomposites every frame (idle main-thread stalls of 40-70 ms, taps 180-330 ms on iPhone-class WebKit). */
.page-progress:not(.page-progress--visible) * { animation-play-state: paused }
`;
    document.head.appendChild(style);
  }

  ensureContainer() {
    if (this.wrapper) return;
    this.ensureStyles();

    /** @type {HTMLElementWithStyle} */
    const wrapper = /** @type {any} */ (document.createElement('div'));
    wrapper.className = 'global-nav-loading page-progress';
    wrapper.setAttribute('role', 'progressbar');
    wrapper.setAttribute('aria-valuemin', '0');
    wrapper.setAttribute('aria-valuemax', '100');
    wrapper.setAttribute('aria-label', 'Page loading bar');

    /** @type {HTMLElementWithStyle} */
    const progress = /** @type {any} */ (document.createElement('div'));
    progress.className = 'page-progress__progress';

    const barBackground = document.createElement('div');
    barBackground.className = 'page-progress__barBackground';

    const tipContainer = document.createElement('div');
    tipContainer.className = 'page-progress__tipContainer';
    const tip = document.createElement('div');
    tip.className = 'page-progress__tip';
    tipContainer.appendChild(tip);

    const glowContainer = document.createElement('div');
    glowContainer.className = 'page-progress__glowContainer';
    for (let i = 1; i <= 7; i += 1) {
      const glow = document.createElement('div');
      glow.className = `page-progress__glow g${i}`;
      glowContainer.appendChild(glow);
    }

    const gradientWrapper = document.createElement('div');
    gradientWrapper.className = 'page-progress__gradientWrapper';
    const gradient = document.createElement('div');
    gradient.className = 'page-progress__gradientAnimation';
    gradientWrapper.appendChild(gradient);

    progress.appendChild(barBackground);
    progress.appendChild(tipContainer);
    progress.appendChild(glowContainer);
    progress.appendChild(gradientWrapper);

    wrapper.appendChild(progress);
    document.documentElement.appendChild(wrapper);

    this.wrapper = wrapper;
    this.progressEl = progress;
  }

  /**
   * @param {number} progressFraction
   */
  set(progressFraction) {
    this.ensureContainer();
    const clamped = Math.max(0, Math.min(1, progressFraction));
    const percent = Math.round(clamped * 100);
    this.progress = clamped;
    if (this.wrapper) this.wrapper.setAttribute('aria-valuenow', String(percent));
    if (this.progressEl) {
      const translateX = -100 + percent;
      this.progressEl.style.transform = `translateX(${translateX}%)`;
      const now = performance.now();
      const delta = clamped - this.lastProgress;
      const dt = now - this.lastSetAt;
      if (delta > 0.08 && dt < 250) {
        this.progressEl.classList.add('page-progress__progress--rushing');
      } else {
        this.progressEl.classList.remove('page-progress__progress--rushing');
      }
      this.lastSetAt = now;
      this.lastProgress = clamped;
    }
  }

  start() {
    if (this.isStarted) return;
    this.ensureContainer();
    this.isStarted = true;
    this.startedAt = performance.now();
    if (this.wrapper) this.wrapper.classList.add('page-progress--visible');
    if (this.progressEl) {
      this.progressEl.classList.remove('page-progress__progress--complete');
      this.progressEl.classList.remove('page-progress__progress--instant');
    }
    this.set(0.08);
    this._beginTrickle();
  }

  _beginTrickle() {
    this._clearTrickle();
    const trickle = () => {
      if (!this.isStarted) return;
      const remaining = 1 - this.progress;
      const delta = Math.random() * Math.min(remaining * 0.25, 0.1);
      const next = Math.min(this.progress + delta, 0.95);
      this.set(next);
      const delay = 80 + Math.random() * 140;
      this.trickleTimer = window.setTimeout(trickle, delay);
    };
    this.trickleTimer = window.setTimeout(trickle, 60);
  }

  _clearTrickle() {
    if (this.trickleTimer) {
      clearTimeout(this.trickleTimer);
      this.trickleTimer = null;
    }
  }

  done() {
    if (!this.isStarted) return;
    this._clearTrickle();
    if (this.progressEl) this.progressEl.classList.add('page-progress__progress--complete');
    this.set(1);
    const elapsed = performance.now() - this.startedAt;
    const extraDelay = Math.max(0, this.minVisibleMs - elapsed);
    const hideAfter = 200 + extraDelay;
    const resetAfterHidden = () => {
      if (!this.wrapper || !this.progressEl) return;
      this.progressEl.classList.add('page-progress__progress--instant');
      this.progressEl.style.transform = 'translateX(-100%)';
      this.progress = 0;
      this.isStarted = false;
    };

    /**
     * @param {TransitionEvent} event
     */
    const onTransitionEnd = (event) => {
      if (event.propertyName !== 'opacity') return;
      if (!this.wrapper) return;
      this.wrapper.removeEventListener('transitionend', onTransitionEnd);
      resetAfterHidden();
    };

    window.setTimeout(() => {
      if (!this.wrapper) return;
      this.wrapper.addEventListener('transitionend', onTransitionEnd);
      this.wrapper.classList.remove('page-progress--visible');
      window.setTimeout(resetAfterHidden, 1600);
    }, hideAfter);
  }
}

// SWATI: Horizon v4's cart-items runs its own same-document view transitions: the first line
// landing in an empty cart drawer, and the last line removed from the drawer or the cart page.
// v2 had none of them, so live never showed the bar there. The shim below skips these types.
const CART_TRANSITION_TYPES = ['fill-cart-drawer', 'empty-cart-drawer', 'empty-cart-page'];

const PageProgress = new PageProgressBar();

export default PageProgress;
// Expose globally for non-module callers
// @ts-ignore
window.PageProgress = PageProgress;

// If an early intent was set before this module loaded, honor it immediately
try {
  const intent = /** @type {any} */ (window).__pageProgressIntent;
  if (intent?.active) {
    try { PageProgress.ensureContainer?.(); } catch (_) {}
    try { PageProgress.start(); } catch (_) {}
  }
} catch (_) {}

// Only show the bar during View Transitions (not regular hard navigations)
// Start on pageswap, complete after pagereveal finishes
(() => {
  /**
   * @template {Event} T
   * @param {T} event
   * @returns {event is T & { viewTransition: ViewTransition }}
   */
  function hasViewTransition(event) {
    // @ts-ignore - ViewTransition type
    return 'viewTransition' in event && event.viewTransition instanceof ViewTransition;
  }

  // Prefer theme-specific events fired when our startViewTransition wrapper runs
  window.addEventListener('theme:viewtransition:start', () => {
    try { PageProgress.start(); } catch (_) {}
  });

  window.addEventListener('theme:viewtransition:end', () => {
    try { PageProgress.done(); } catch (_) {}
  });

  // Fallback to native VT events if present
  window.addEventListener('pageswap', (event) => {
    if (!hasViewTransition(event)) return;
    try { PageProgress.start(); } catch (_) {}
  });

  window.addEventListener('pagereveal', (event) => {
    if (!hasViewTransition(event)) return;
    const { viewTransition } = event;
    try { PageProgress.start(); } catch (_) {}
    Promise.resolve(viewTransition.finished).then(() => {
      try { PageProgress.done(); } catch (_) {}
    });
  });

  // Ensure bar is reset on hard navigations
  // Avoid forcing done() on pageshow; the new document manages its own lifecycle

  // Shim: wrap native document.startViewTransition to ensure progress bar shows
  try {
    const originalStartVT = document.startViewTransition?.bind(document);
    // @ts-ignore - add a private flag on document to avoid double patching
    const alreadyPatched = /** @type {any} */ (document).__pageProgressPatched;
    if (originalStartVT && !alreadyPatched) {
      // @ts-ignore
      /** @type {any} */ (document).__pageProgressPatched = true;
      // @ts-ignore
      document.startViewTransition = (callback) => {
        const vt = originalStartVT(callback);
        // utilities.js adds the types right after this call returns, so read them a microtask
        // later. That is still before the old state is captured on the next frame.
        queueMicrotask(() => {
          const types = /** @type {any} */ (vt)?.types ? Array.from(/** @type {any} */ (vt).types) : [];
          if (types.some((type) => CART_TRANSITION_TYPES.includes(type))) return;
          try { PageProgress.start(); } catch (_) {}
          Promise.resolve(vt?.finished).finally(() => {
            try { PageProgress.done(); } catch (_) {}
          });
        });
        return vt;
      };
    }
  } catch (_) {}
})();


